import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import './SignUp.css';
import { useDispatch, useSelector } from 'react-redux';
import { registerUser } from '../../rtk/thunks/authThunk';
import { FaEye } from 'react-icons/fa';
import { FaEyeSlash } from "react-icons/fa6";
import countries from '../../utils/countries';

const SignUp = () => {
    const dispatch = useDispatch();
    const { loading, error: submitError, message } = useSelector((state) => state.auth);
    const [showPassword, setShowPassword] = useState(false);
    const [localError, setLocalError] = useState('');

    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        country: '',
        password: '',
        confirmPassword: '',
    });


    const formatError = (value) => {
        if (!value) return '';
        if (typeof value === 'string') return value;
        if (Array.isArray(value)) return value.filter(Boolean).join(' ');

        return (
            value.detail ||
            value.message ||
            value.non_field_errors?.[0] ||
            value.email?.[0] ||
            value.password?.[0] ||
            value.first_name?.[0] ||
            value.last_name?.[0] ||
            value.country?.[0] ||
            Object.values(value)
                .flat()
                .find((item) => typeof item === 'string' && item.trim()) ||
            'Registration failed. Please try again.'
        );
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLocalError('');
        if (formData.password !== formData.confirmPassword) {
            setLocalError('Passwords do not match.');
            return;
        }
        try {
            await dispatch(registerUser({
                first_name: formData.first_name,
                last_name: formData.last_name,
                email: formData.email,
                country: formData.country,
                password: formData.password,
            })).unwrap();

            setFormData({
                first_name: '',
                last_name: '',
                email: '',
                country: '',
                password: '',
                confirmPassword: '',
            });
            localStorage.setItem('pending_verification_email', formData.email);
            window.location.href = '/verify-email';
        } catch (err) {
            // errors surfaced from slice via submitError
        }
    };

    return (
        <div className="signup-container">
            {/* The info section is the same as the sign-in page */}
            <div className="info-section">
                <div className="info-content">
                    <h1 className="title">
                        <span role="img" aria-label="target" className="target-icon">🎯</span>
                        Prepare Smarter for CPHQ – With AI On Your Side
                    </h1>
                    <p className="subtitle">Your Personalized CPHQ Prep Platform</p>
                    
                    <ul className="features-list">
                        <li>
                          <span className="feature-icon-frame"><img src="/assets/why/study.png" alt="" className="feature-image" /></span>
                            <div className='feature-list'>
                                <h2>AI Study Planner</h2>
                                <p>Personalized study goals and reminders</p>
                            </div>
                        </li>
                        <li>
                            <span className="feature-icon-frame"><img src="/assets/why/study.png" alt="" className="feature-image" /></span>
                            <div className='feature-list'>
                                <h2>Interactive Content</h2>
                                <p>Flashcards, videos, summaries</p>
                            </div>
                        </li>
                        <li>
                          <span className="feature-icon-frame"><img src="/assets/why/Mock.png" alt="" className="feature-image" /></span>
                            <div className='feature-list'>
                                <h2>Mock Exams</h2>
                                <p>Adaptive quizzes and smart scoring</p>
                            </div>
                        </li>
                        <li>
                         <span className="feature-icon-frame"><img src="/assets/why/performancedashboard.png" alt="" className="feature-image" /></span>
                            <div className='feature-list'>
                                <h2>Performance Dashboard</h2>
                                <p>Visual progress tracking</p>
                            </div>
                        </li>
                        <li>
                            <span className="feature-icon-frame"><img src="/assets/why/aitutor.png" alt="" className="feature-image" /></span>
                            <div className='feature-list'>
                                <h2>AI Tutor</h2>
                                <p>Ask anything, anytime — Built on GPT-4o</p>
                            </div>
                        </li>
                        <li>
                         <span className="feature-icon-frame"><img src="/assets/why/readness.png" alt="" className="feature-image" /></span>
                            <div className='feature-list'>
                                <h2>Readiness Meter</h2>
                                <p>Know when you're ready to pass</p>
                            </div>
                        </li>
                    </ul>
                    <div className='footer-text'>
                       <p>Built with healthcare professionals in mind. HIPAA-aware. Secure. Mobile-first. Accessible.</p>
                    </div>
                </div>
            </div>

            {/* The form section is new for the sign-up page */}
            <div className="form-section">
                <div className="form-content">
                    <h2>Sign up</h2>
                    <p className="login-link">
                        Already have an account? <NavLink to="/sign-in">Sign In</NavLink>
                    </p>

                    <form onSubmit={handleSubmit}>
                        <div className="name-group">
                            <div className="input-group">
                                <input
                                    type="text"
                                    name="first_name"
                                    placeholder="First name"
                                    value={formData.first_name}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                            <div className="input-group">
                                <input
                                    type="text"
                                    name="last_name"
                                    placeholder="Last name"
                                    value={formData.last_name}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>

                        <div className="input-group">
                            <input
                                type="email"
                                name="email"
                                placeholder="Email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                            />
                        </div>
                        
                        <div className="input-group">
                            <select
                                name="country"
                                value={formData.country}
                                onChange={handleChange}
                                required
                            >
                                <option value="" disabled>Country</option>
                                {countries.map((country) => (
                                    <option key={country} value={country}>{country}</option>
                                ))}
                            </select>
                        </div>
                        
                        <h3 className="password-title">Set Password</h3>
                        <div className="input-group">
                            <input
                                type={showPassword ? "text" : "password"}
                                name="password"
                                placeholder="Password"
                                value={formData.password}
                                onChange={handleChange}
                                required
                            />
                            {
                                showPassword ? (
                                <FaEyeSlash className="password-icon" onClick={() => setShowPassword(false)} />
                                   ) : (
                                 <FaEye className="password-icon" onClick={() => setShowPassword(true)} />
                                   )
                                 }
                        </div>
                        <div className="input-group">
                            <input
                                type={showPassword ? "text" : "password"}
                                name="confirmPassword"
                                placeholder="Confirm Password"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                required
                            />
                            {
                                showPassword ? (
                                    <FaEyeSlash className="password-icon" onClick={() => setShowPassword(false)} />
                                ) : (
                                    <FaEye className="password-icon" onClick={() => setShowPassword(true)} />
                                )
                            }
                        </div>

                        {localError && <p className="form-error">{localError}</p>}
                        {submitError && <p className="form-error">{formatError(submitError)}</p>}
                        {message && <p className="form-success">{message}</p>}

                        <button type="submit" className="submit-button" disabled={loading}>
                            {loading ? 'Signing up...' : 'Sign Up'}
                        </button>

                        <div className="agreement-group">
                            <input type="checkbox" id="agreement" required />
                            <label htmlFor="agreement">
                                I agree to the <a href="#">Master Subscription Agreement</a>
                            </label>
                        </div>
                    </form>

                    <p className="privacy-notice">
                        By registering, you agree to the processing of your personal data by Nexorah AI as described in the Privacy Statement.
                    </p>
                </div>
            </div>
        </div> 
    ); 
} 

export default SignUp;
